/**
 * One checkbox with its label, and an optional description under the label. The whole row is the
 * target, so the 20px box never has to be hit on its own.
 *
 * `indeterminate` is the "some of these" state of a box that controls a list. It shows a dash and a
 * screen reader hears "mixed". An `error` sits under the row and is the box's description, for a
 * box that must be ticked to go on, such as agreeing to terms.
 *
 * Disabled is `aria-disabled`, so the box stays in the tab order and its description, which should
 * say why, is still read.
 */
import * as stylex from "@stylexjs/stylex";
import type { ReactNode } from "react";
import { useEffect, useId, useRef } from "react";
import "@lattice/tokens/color.stylex";
import { color } from "@lattice/tokens/color.stylex";
import "@lattice/tokens/size.stylex";
import { size } from "@lattice/tokens/size.stylex";
import "@lattice/tokens/space.stylex";
import { radius, space } from "@lattice/tokens/space.stylex";
import "@lattice/tokens/text.stylex";
import { text } from "@lattice/tokens/text.stylex";
import "@lattice/tokens/breakpoints.stylex";
import { breakpoints } from "@lattice/tokens/breakpoints.stylex";
import { choiceStyle } from "./choice.ts";
import { focusRing } from "./focus.ts";
import { CheckIcon, MinusIcon, WarningIcon } from "./icons.tsx";
import { textStyle } from "./textStyle.ts";
const styles = {
  row: {
    k8WAf4: "xpdwd9g x93fw87",
    k1xSpc: "x78zum5",
    kGNEyG: "x1cy8zhl",
    kOIVth: "x1173whf",
    kkrTdU: "x1ypdohk",
    kMwMTN: "xm8xz4y",
    $$css: true
  },
  rowDisabled: {
    kkrTdU: "x1h6gzvc",
    kMwMTN: "x16nvyyk",
    $$css: true
  },
  box: {
    kzqmXN: "xh8yej3",
    kZKoxP: "x5yr21d",
    kaIpWk: "x8wwnw1",
    $$css: true
  },
  description: {
    kZCmMZ: "x15lra4c",
    kMwMTN: "x1lm7cez",
    k63SB2: "xox936i",
    $$css: true
  },
  error: {
    kZCmMZ: "x15lra4c",
    k1xSpc: "x78zum5",
    kGNEyG: "x1cy8zhl",
    kOIVth: "xskm3c9",
    kogj98: "x1ghz6dp",
    kMwMTN: "x6ygkrl",
    $$css: true
  }
};
export interface CheckboxProps {
  readonly label: ReactNode;
  readonly checked: boolean;
  readonly onChange: (checked: boolean) => void;
  readonly description?: ReactNode;
  readonly indeterminate?: boolean;
  readonly disabled?: boolean;
  readonly error?: string | null;
  readonly name?: string;
  readonly value?: string;
  readonly id?: string;
  readonly testId?: string;
}
export function Checkbox({
  label,
  checked,
  onChange,
  description,
  indeterminate = false,
  disabled = false,
  error,
  name,
  value,
  id,
  testId
}: CheckboxProps) {
  const made = useId();
  const control = id ?? made;
  const box = useRef<HTMLInputElement>(null);
  const failed = error !== undefined && error !== null && error !== "";
  const on = checked || indeterminate;

  /* `indeterminate` is a property with no attribute, so it can only be set from script. */
  useEffect(() => {
    if (box.current) box.current.indeterminate = indeterminate;
  }, [indeterminate]);
  const describedBy = [description === undefined ? null : `${control}-description`, failed ? `${control}-error` : null].filter(part => part !== null).join(" ");
  return <div className="x78zum5 xdt5ytf x15xv0s5 xeuugli">
      <label htmlFor={control} {...stylex.props(textStyle.body, styles.row, disabled && styles.rowDisabled)}>
        <span className="x1n2onr6 x78zum5 x2lah0s x1z0q0p5 xq9ob8z x1h3jhxu">
          <input ref={box} id={control} type="checkbox" name={name} value={value} checked={checked} aria-disabled={disabled || undefined} aria-invalid={failed || undefined} aria-describedby={describedBy === "" ? undefined : describedBy} onChange={event => {
          if (!disabled) onChange(event.currentTarget.checked);
        }} data-testid={testId} {...stylex.props(choiceStyle.mark, styles.box, on && choiceStyle.on, failed && !on && choiceStyle.invalid, disabled && choiceStyle.disabled, disabled && on && choiceStyle.disabledOn, focusRing)} />
          {on && <span aria-hidden="true" className="x10l6tqk x10a8y8t x78zum5 xxil687 x47corl">
              {indeterminate ? <MinusIcon /> : <CheckIcon />}
            </span>}
        </span>
        <span className="xeuugli xj0a0fe">{label}</span>
      </label>
      {description !== undefined && <span id={`${control}-description`} {...stylex.props(textStyle.label, styles.description)}>
          {description}
        </span>}
      {failed && <p id={`${control}-error`} role="alert" {...stylex.props(textStyle.label, styles.error)}>
          <WarningIcon />
          <span>{error}</span>
        </p>}
    </div>;
}